/**
 * InstallPromptScreen — Shows install instructions or triggers browser prompt.
 *
 * UI-INV-005: Leaf component, props only.
 * Chrome/Android: browser handles the prompt (triggered by parent via beforeinstallprompt).
 * iOS/Safari: manual 3-step instructions (Share → Add to Home Screen → Add).
 */
import styles from "./InstallPromptScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Props = {
  isIOS: boolean;
  onDone: () => void;
  onBack: () => void;
};

export function InstallPromptScreen({ isIOS, onDone, onBack }: Props) {
  return (
    <div className={styles.screen} data-testid={TEST_IDS.INSTALL_PROMPT_SCREEN}>
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onBack} type="button" data-testid={TEST_IDS.BACK_BTN}>
          ← back
        </button>
        <span className={styles.metaLabel}>Add to home screen</span>
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>
          {isIOS ? "Three steps." : "Almost there."}
        </h1>

        {isIOS ? (
          <>
            {/* Visual indicator: pulsing arrow pointing to Safari's bottom toolbar */}
            <div className={styles.shareHint} aria-hidden="true">
              <div className={styles.shareBox}>
                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3v12M7 8l5-5 5 5" />
                  <path d="M5 16v2a2 2 0 002 2h10a2 2 0 002-2v-2" />
                </svg>
                <span className={styles.shareBoxLabel}>Share</span>
              </div>
              <div className={styles.shareArrow} aria-hidden="true">↑</div>
              <div className={styles.shareBarMock} aria-hidden="true" />
            </div>

            <div className={styles.steps}>
              <div className={styles.step}>
                <div className={styles.stepNum}>1</div>
                <div className={styles.stepText}>
                  Tap the <strong>Share</strong> button in Safari's bottom toolbar
                </div>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNum}>2</div>
                <div className={styles.stepText}>
                  Scroll down and tap <strong>Add to Home Screen</strong>
                </div>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNum}>3</div>
                <div className={styles.stepText}>
                  Tap <strong>Add</strong> in the top right corner
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className={styles.intro}>
            Follow your browser's prompt to install. The app will appear on
            your home screen and run offline.
          </p>
        )}

        <div className={styles.ctas}>
          <PrimaryAction onClick={onDone} testId={TEST_IDS.INSTALL_PROMPT_DONE_BTN}>
            {isIOS ? "I've added it to my home screen" : "Done"}
          </PrimaryAction>
          <SecondaryAction onClick={onBack}>Back</SecondaryAction>
        </div>
      </div>
    </div>
  );
}
